/**
 * htmlJsonMergeUtil_Conversion.ts - Conversion utilities for HtmlJsonMergeUtil
 * Pure ES module - works in both Node.js and browser via bundler
 */

import { JsonArray, JsonObject, JsonValue } from '../../../../Assembler/dist/app/json/jsonValue.js';

// [Dafny Verified] Properties S1-S4 — Verification/dafny/src/ConvertToString.dfy
/**
 * Converts a JsonValue to string representation (delegates to JsonValue.toStringOrEmpty)
 */
export function convertToString(value: JsonValue): string {
    return value.isNull ? 'null' : value.toStringOrEmpty();
}

/**
 * Merges contextJson into dataJson. Arrays use smart merge (append/skip) when
 * ArrayNamePrimary is found in contextJson; otherwise contextJson overwrites.
 */
export function mergeContextIntoDataJson(dataJson: JsonObject, contextJson: JsonObject): JsonObject {
    const merged = new JsonObject();
    for (const [key, value] of dataJson) {
        merged.set(key, value);
    }
    for (const [key, value] of contextJson) {
        const primaryKeyName = key + 'Primary';
        if (value.isArray && contextJson.has(primaryKeyName) && contextJson.get(primaryKeyName).isString
            && merged.has(key) && merged.get(key).isArray) {
            // Smart merge: build new array to avoid mutating shared cached data
            const primaryKey = contextJson.get(primaryKeyName).asString();
            const existingArray = merged.get(key).getArray();
            const contextArray = value.getArray();
            const newArray = new JsonArray();
            newArray.addRange(existingArray);
            for (let g = 0; g < contextArray.count; g++) {
                const contextItem = contextArray.get(g);
                if (!contextItem.isObject) continue;
                const contextObj = contextItem.getObject();

                let exists = false;
                for (let c = 0; c < newArray.count; c++) {
                    const existingItem = newArray.get(c);
                    if (!existingItem.isObject) continue;
                    if (MatchesByStringKeys(existingItem.getObject(), contextObj, primaryKey)) {
                        exists = true;
                        break;
                    }
                }
                if (!exists) {
                    newArray.add(contextItem);
                }
            }
            merged.set(key, newArray);
        } else {
            merged.set(key, value);
        }
    }
    return merged;
}

/**
 * Executes GetScalarString for this AssemblerJson workflow.
 */
function GetScalarString(val: JsonValue): string | null {
    if (val.isString) return val.asString();
    if (val.isInteger) return val.asInteger()?.toString() ?? null;
    if (val.isNumber) return val.asNumber()?.toString() ?? null;
    if (val.isBool) return val.asBool() === true ? 'true' : 'false';
    return null;
}

/**
 * Executes MatchesByStringKeys for this AssemblerJson workflow.
 */
function MatchesByStringKeys(templateItem: JsonObject, contextItem: JsonObject, primaryKey: string | null): boolean {
    if (primaryKey !== null) {
        if (!templateItem.has(primaryKey) || !contextItem.has(primaryKey)) return false;
        const templateStr = GetScalarString(templateItem.get(primaryKey));
        const contextStr = GetScalarString(contextItem.get(primaryKey));
        return templateStr !== null && contextStr !== null && templateStr === contextStr;
    }

    let hasScalarKey = false;
    for (const [key, value] of contextItem) {
        const contextStr = GetScalarString(value);
        if (contextStr === null) continue;
        hasScalarKey = true;
        if (!templateItem.has(key)) return false;
        const templateStr = GetScalarString(templateItem.get(key));
        if (templateStr === null) return false;
        if (templateStr !== contextStr) return false;
    }
    return hasScalarKey;
}
